/**
 * Serie A Data Hub API — Math&Sport B2B
 * Base URL: https://mathandsport-b2b.seriea.prd.sdp.deltatre.digital
 * Path pattern: /v1/{projectCode}/football/...
 * Auth: x-api-key header
 */

const BASE_URL = 'https://mathandsport-b2b.seriea.prd.sdp.deltatre.digital';
const PROJECT_CODE = process.env.REACT_APP_PROJECT_CODE || 'serie-a';

function root() {
  return `${BASE_URL}/v1/${PROJECT_CODE}/football`;
}

function getApiKey() {
  return process.env.REACT_APP_SERIEA_API_KEY || '';
}

async function apiFetch(path, params = {}) {
  const url = new URL(`${root()}${path}`);

  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v));
  });

  const headers = { 'Accept': 'application/json' };
  const apiKey = getApiKey();
  if (apiKey) headers['x-api-key'] = apiKey;

  if (process.env.NODE_ENV === 'development') {
    console.log(`[SerieA API] GET ${url.toString()}`);
  }

  let response;
  try {
    response = await fetch(url.toString(), { headers });
  } catch (e) {
    throw new Error(`Errore di rete (CORS?): ${e.message}`);
  }

  if (!response.ok) {
    let body = '';
    try { body = await response.text(); } catch {}
    throw new Error(`API Error ${response.status}${body ? ': ' + body.slice(0, 200) : ''}`);
  }

  const text = await response.text();
  if (!text) return {};
  try { return JSON.parse(text); } catch {
    throw new Error('Risposta non JSON');
  }
}

// ─── Competitions ─────────────────────────────────────────────────────────────
// GET /v1/{projectCode}/football/competitions
export async function getCompetitions() {
  const data = await apiFetch('/competitions');
  return data.competitions || data.data || (Array.isArray(data) ? data : []);
}

// GET /v1/{projectCode}/football/competitions/{competitionId}
export async function getCompetition(competitionId) {
  const data = await apiFetch(`/competitions/${encodeURIComponent(competitionId)}`);
  return data.competition || data.data || data;
}

// ─── Seasons ──────────────────────────────────────────────────────────────────
// GET /v1/{projectCode}/football/competitions/{competitionId}/seasons
export async function getSeasonsByCompetition(competitionId) {
  const data = await apiFetch(`/competitions/${encodeURIComponent(competitionId)}/seasons`);
  return data.seasons || data.data || (Array.isArray(data) ? data : []);
}

// GET /v1/{projectCode}/football/competitions/{competitionId}/season/{seasonId}
export async function getSeason(competitionId, seasonId) {
  const data = await apiFetch(
    `/competitions/${encodeURIComponent(competitionId)}/season/${encodeURIComponent(seasonId)}`
  );
  return data.season || data.data || data;
}

// ─── Teams ────────────────────────────────────────────────────────────────────
// GET /v1/{projectCode}/football/seasons/{seasonId}/teams  (se esiste)
export async function getTeamsBySeason(seasonId) {
  const data = await apiFetch(`/seasons/${encodeURIComponent(seasonId)}/teams`);
  return data.teams || data.data || (Array.isArray(data) ? data : []);
}

// ─── Matchdays ────────────────────────────────────────────────────────────────
// GET /v1/{projectCode}/football/seasons/{seasonId}/matchdays
export async function getMatchdaysBySeason(seasonId) {
  const data = await apiFetch(`/seasons/${encodeURIComponent(seasonId)}/matchdays`);
  return data.matchdays || data.data || (Array.isArray(data) ? data : []);
}

// ─── Matches ─────────────────────────────────────────────────────────────────
// GET /v1/{projectCode}/football/seasons/{seasonId}/matches
export async function getMatchesBySeason(seasonId, { matchDayId } = {}) {
  const params = {};
  if (matchDayId) params.matchDayId = matchDayId;
  const data = await apiFetch(`/seasons/${encodeURIComponent(seasonId)}/matches`, params);
  if (Array.isArray(data)) return data;
  return data.matches || data.matchSets || data.data || [];
}

// GET /v1/{projectCode}/football/seasons/{seasonId}/matches/current
export async function getCurrentMatch(seasonId) {
  const data = await apiFetch(`/seasons/${encodeURIComponent(seasonId)}/matches/current`);
  return data.match || data.data || data;
}

// GET /v1/{projectCode}/football/seasons/{seasonId}/matches/{matchId}/live
export async function getMatchLive(seasonId, matchId) {
  const data = await apiFetch(
    `/seasons/${encodeURIComponent(seasonId)}/matches/${encodeURIComponent(matchId)}/live`
  );
  return data.match || data.liveData || data.data || data;
}

// ─── Player Stats (per MVP) ───────────────────────────────────────────────────
/**
 * GET /v1/{projectCode}/football/seasons/{seasonId}/match/{matchId}/playerstats
 * Restituisce le statistiche dei giocatori per una partita specifica.
 * Usato come input per il motore MVP (PlayerRankingEngine).
 */
export async function getMatchPlayerStats(seasonId, matchId) {
  // Il projectCode va estratto dal seasonId se contiene "::"
  const projectCode = (typeof seasonId === 'string' && seasonId.includes('::'))
    ? seasonId.split('::')[0]
    : (process.env.REACT_APP_PROJECT_CODE || 'serie-a');

  const url = new URL(
    `${BASE_URL}/v1/${projectCode}/football/seasons/${encodeURIComponent(seasonId)}/match/${encodeURIComponent(matchId)}/playerstats`
  );

  const headers = { 'Accept': 'application/json' };
  const apiKey = getApiKey();
  if (apiKey) headers['x-api-key'] = apiKey;

  if (process.env.NODE_ENV === 'development') {
    console.log(`[SerieA API] GET ${url.toString()}`);
  }

  let response;
  try {
    response = await fetch(url.toString(), { headers });
  } catch (e) {
    throw new Error(`Errore di rete: ${e.message}`);
  }

  if (!response.ok) {
    let body = '';
    try { body = await response.text(); } catch {}
    throw new Error(`PlayerStats API Error ${response.status}${body ? ': ' + body.slice(0, 200) : ''}`);
  }

  const text = await response.text();
  if (!text) return {};
  try { return JSON.parse(text); } catch {
    throw new Error('Risposta PlayerStats non JSON');
  }
}

// ─── Event data endpoints (per Event Explorer plugin) ────────────────────────

export async function getMatchHeader(seasonId, matchId) {
  return apiFetch(`/seasons/${encodeURIComponent(seasonId)}/matches/${encodeURIComponent(matchId)}/header`);
}

export async function getMatchLineups(seasonId, matchId) {
  return apiFetch(`/seasons/${encodeURIComponent(seasonId)}/matches/${encodeURIComponent(matchId)}/lineups`);
}

export async function getMatchFeedPage(seasonId, matchId, page = 1) {
  return apiFetch(
    `/seasons/${encodeURIComponent(seasonId)}/matches/${encodeURIComponent(matchId)}/feed`,
    { page }
  );
}

export async function getAllFeedEvents(seasonId, matchId, onProgress) {
  const first = await getMatchFeedPage(seasonId, matchId, 1);
  const totalPages = first.pagination?.totalPages || first.totalPages || 1;
  let all = [...(first.events || first.dataModel || first.data || [])];
  for (let p = 2; p <= totalPages; p++) {
    if (onProgress) onProgress(p, totalPages);
    const res = await getMatchFeedPage(seasonId, matchId, p);
    all = all.concat(res.events || res.dataModel || res.data || []);
  }
  return all;
}

export async function getFifaStandardEventsPage(matchId, page = 1) {
  const PC = process.env.REACT_APP_PROJECT_CODE || 'serie-a';
  const url = new URL(`${BASE_URL}/v1/${PC}/football/match/${encodeURIComponent(matchId)}/FIFAStandardEvents`);
  url.searchParams.set('page', page);
  url.searchParams.set('pageNumElement', '100');
  const headers = { 'Accept': 'application/json' };
  const apiKey = getApiKey();
  if (apiKey) headers['x-api-key'] = apiKey;
  const response = await fetch(url.toString(), { headers });
  if (!response.ok) throw new Error(`FIFA Events API ${response.status}`);
  return response.json();
}

export async function getAllFifaEvents(matchId, onProgress) {
  const first = await getFifaStandardEventsPage(matchId, 1);
  const totalPages = first.pagination?.totalPages || 1;
  let all = [...(first.dataModel || [])];
  for (let p = 2; p <= totalPages; p++) {
    if (onProgress) onProgress(p, totalPages);
    const res = await getFifaStandardEventsPage(matchId, p);
    all = all.concat(res.dataModel || []);
  }
  return all;
}
